import { format, isValid, startOfDay } from "date-fns";
import { formatA3PartyLabel } from "../../utils/a3PedidosListUtils";
import { normalizeEmpresaA3 } from "../../constants/a3Empresas";

export const NO_ARTICLE_CODE = "__NO_ARTICLE_CODE__";

export const startOfToday = () => startOfDay(new Date());

export const toYmd = (d) => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (!isValid(date)) return "";
  return format(date, "yyyy-MM-dd");
};

export const fmtA3Cantidad = (v) => {
  if (v === undefined || v === null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("es-ES", { maximumFractionDigits: 0 });
};

export const fmtA3Importe = (v) => {
  if (v === undefined || v === null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "—";
};

export const resolveUnitPriceForDisplay = (
  unidades,
  precioUn,
  total,
  totalDocumento,
) => {
  const u = Number(unidades);
  const p = Number(precioUn);
  const t = Number(total);
  const td = Number(totalDocumento);
  const hasU = Number.isFinite(u) && u > 0;
  const hasP = Number.isFinite(p);
  const hasTD = Number.isFinite(td);
  const baseTotal = hasTD ? td : t;
  const hasBaseTotal = Number.isFinite(baseTotal);
  if (!hasU) return hasP ? p : null;
  if (!hasP && hasBaseTotal) return Number((baseTotal / u).toFixed(6));
  if (hasP && hasBaseTotal) {
    const implied = Number((baseTotal / u).toFixed(6));
    if (Number.isFinite(implied) && Math.abs(implied - p) > 0.01)
      return implied;
  }
  return hasP ? p : hasBaseTotal ? Number((baseTotal / u).toFixed(6)) : null;
};

export const calcResumenMargenGrupo = (compra, venta, monedaCompra, monedaVenta) => {
  const c = Number(compra);
  const v = Number(venta);
  if (!Number.isFinite(c) || !Number.isFinite(v)) return null;
  const mc = String(monedaCompra || "").trim();
  const mv = String(monedaVenta || "").trim();
  if (mc && mv && mc !== mv) return null;
  const abs = v - c;
  const pct = v > 0 ? (abs / v) * 100 : null;
  return { abs, pct };
};

export const isA3Served = (estado, detalle) => {
  const s = String(estado || "")
    .trim()
    .toUpperCase();
  const d = String(detalle || "")
    .trim()
    .toUpperCase();
  return s === "S" || d === "S" || s.includes("SERV") || d.includes("SERV");
};

export const fmtA3Estado = (estado, detalle) => {
  const s = String(estado || "").trim();
  const d = String(detalle || "").trim();
  if (!s && !d) return "";
  return isA3Served(estado, detalle) ? "S" : "A";
};

export function totalesPedidosGrupo(pedidos) {
  let docCompra = 0;
  let docVenta = 0;
  let filasConCompra = 0;
  let filasConVenta = 0;
  for (const row of pedidos) {
    if (row?.IdPedc != null && Number(row.IdPedc) > 0) {
      filasConCompra += 1;
      const x = Number(row.TotalDocumentoCompra);
      if (Number.isFinite(x)) docCompra += x;
    }
    if (row?.IdPedv != null && Number(row.IdPedv) > 0) {
      filasConVenta += 1;
      const x = Number(row.TotalDocumentoVenta);
      if (Number.isFinite(x)) docVenta += x;
    }
  }
  return { docCompra, docVenta, filasConCompra, filasConVenta };
}

export const getPoCompraA3 = (row) =>
  row?.SerieCompra != null && row?.NumDocCompra != null
    ? `${String(row.TipoContableCompra || "1").trim()}/${String(row.SerieCompra).trim()}-${row.NumDocCompra}`
    : "";

export const getPoVentaA3 = (row) =>
  row?.SerieVenta != null && row?.NumDocVenta != null
    ? `${String(row.TipoContableVenta || "1").trim()}/${String(row.SerieVenta).trim()}-${row.NumDocVenta}`
    : "";

export function agruparPorArticulo(rows) {
  const map = new Map();
  for (const row of rows) {
    const cod = String(row.CodArt || "").trim() || NO_ARTICLE_CODE;
    if (!map.has(cod)) map.set(cod, []);
    map.get(cod).push(row);
  }
  return Array.from(map.entries()).map(([codArt, pedidos]) => ({
    codArt,
    pedidos,
  }));
}

export const PRODUCCION_TREE_SIN_CLIENTE = "__SIN_CLIENTE__";

export function getProduccionPedidoTreeId(row) {
  const n = Number(row?.IdPresupuestoA3Pedido);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function getProduccionEmpresaKey(row) {
  const v = String(row?.EmpresaA3 || "").trim().toUpperCase();
  if (!v) return "";
  return normalizeEmpresaA3(v, v);
}

export function getProduccionEmpresaShort(raw) {
  const key = String(raw || "").trim().toUpperCase();
  if (!key) return "";
  const norm = normalizeEmpresaA3(key, key);
  if (norm === "STAGE_DESIGN") return "STAGE";
  if (norm === "ERNA_TRADE") return "ERNA";
  if (norm === "NML_SPAIN") return "NML";
  return norm;
}

export function getProduccionEmpresaTreeAccent(raw) {
  const key = String(raw || "").trim().toUpperCase();
  const norm = key ? normalizeEmpresaA3(key, key) : "";
  if (norm === "STAGE_DESIGN") return "#1976d2";
  if (norm === "ERNA_TRADE") return "#aa19c4";
  if (norm === "NML_SPAIN") return "#2e7d32";
  return "#ed6c02";
}

function pickMostCommonClienteNombre(pedidos) {
  const counts = new Map();
  for (const row of Array.isArray(pedidos) ? pedidos : []) {
    const name = String(row?.ClienteNombre || "").trim();
    if (!name) continue;
    counts.set(name, (counts.get(name) || 0) + 1);
  }
  let best = "";
  let max = -1;
  for (const [name, count] of counts.entries()) {
    if (count > max || (count === max && name.length > best.length)) {
      best = name;
      max = count;
    }
  }
  return best;
}

export function getProduccionClienteKey(row, sinClienteLabel = "Sin cliente") {
  const empresa = getProduccionEmpresaKey(row);
  const cod = String(row?.CodCli ?? "").trim();
  if (cod) {
    const name = String(row?.ClienteNombre || "").trim();
    return {
      key: `${empresa}::${cod}`,
      label: name || formatA3PartyLabel(cod, ""),
      empresa,
      codCli: cod,
    };
  }
  const name = String(row?.ClienteNombre || "").trim();
  if (name) {
    return {
      key: `${empresa}::nombre:${name.toUpperCase()}`,
      label: name,
      empresa,
      codCli: "",
    };
  }
  return {
    key: PRODUCCION_TREE_SIN_CLIENTE,
    label: sinClienteLabel,
    empresa,
    codCli: "",
  };
}

export function getProduccionPedidoTreeDate(row) {
  const ymd = toYmd(
    row?.FechaVentaA3 || row?.FechaCompraA3 || row?.FechaA3 || row?.CreatedAt,
  );
  if (!ymd) return "";
  const [year, month, day] = ymd.split("-").map((part) => Number(part));
  if (!year || !month || !day) return "";
  return `${day}/${month}/${String(year).slice(-2)}`;
}

export function getProduccionPedidoTreeLabel(row) {
  const id = getProduccionPedidoTreeId(row);
  const ref = String(row?.Referencia || "").trim();
  if (id && ref) return `#${id} · ${ref}`;
  if (id) return `#${id}`;
  return ref || "Pedido";
}

export function groupProduccionPedidosByCliente(rows, labels = {}) {
  const sinClienteLabel = labels.sinCliente || "Sin cliente";
  const groups = new Map();
  const ensure = (key, label) => {
    if (!groups.has(key)) groups.set(key, { key, label, pedidos: [] });
    return groups.get(key);
  };

  for (const row of Array.isArray(rows) ? rows : []) {
    const { key, label } = getProduccionClienteKey(row, sinClienteLabel);
    ensure(key, label).pedidos.push(row);
  }

  for (const group of groups.values()) {
    if (group.key === PRODUCCION_TREE_SIN_CLIENTE) {
      group.label = sinClienteLabel;
      group.empresa = "";
      group.empresaShort = "";
      continue;
    }
    const name = pickMostCommonClienteNombre(group.pedidos) || group.label;
    const empresa = getProduccionEmpresaKey(group.pedidos[0]);
    group.empresa = empresa;
    group.empresaShort = getProduccionEmpresaShort(empresa);
    group.label = name;
  }

  for (const group of groups.values()) {
    group.articulos = agruparPorArticulo(group.pedidos).sort((a, b) =>
      String(a.codArt).localeCompare(String(b.codArt), "es"),
    );
  }

  return Array.from(groups.values()).sort((a, b) => {
    if (a.key === PRODUCCION_TREE_SIN_CLIENTE) return 1;
    if (b.key === PRODUCCION_TREE_SIN_CLIENTE) return -1;
    return (
      String(a.label).localeCompare(String(b.label), "es") ||
      String(a.empresa || "").localeCompare(String(b.empresa || ""), "es")
    );
  });
}

export function getProduccionGrupoDePedido(rows, selectedPedidoId) {
  const list = Array.isArray(rows) ? rows : [];
  const selected = list.find(
    (row) => getProduccionPedidoTreeId(row) === Number(selectedPedidoId),
  );
  if (!selected) return null;
  const clienteKey = getProduccionClienteKey(selected).key;
  const codArt = String(selected.CodArt || "").trim() || NO_ARTICLE_CODE;
  const pedidos = list.filter((row) => {
    const sameCliente = getProduccionClienteKey(row).key === clienteKey;
    const art = String(row.CodArt || "").trim() || NO_ARTICLE_CODE;
    return sameCliente && art === codArt;
  });
  return { codArt, pedidos };
}
